/**
 * Reclassification History Modal
 * ==============================
 * Complete audit trail of all reclassifications for a report.
 *
 * Features:
 * - View all reclassifications chronologically
 * - Filter by date range, user, or transaction
 * - Export to CSV for external audit
 * - Full court-defensible documentation
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Search, Filter, Calendar, User, FileText } from 'lucide-react';
import api from '../../utils/api';
import { formatCurrency } from '../../utils/financialFormat';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { FinancialValue } from '../shared/FinancialValue';

interface HistoryModalProps {
  reportId: string;
  onClose: () => void;
}

interface ReclassificationEntry {
  transaction_id: string;
  transaction_date: string;
  vendor: string;
  amount: number;
  from_category: string;
  to_category: string;
  reason: string;
  user: string;
  user_email: string;
  timestamp: string;
}

export default function HistoryModal({ reportId, onClose }: HistoryModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [history, setHistory] = useState<ReclassificationEntry[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<ReclassificationEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month

  useEffect(() => {
    fetchHistory();
  }, [reportId]);

  useEffect(() => {
    applyFilters();
  }, [history, searchQuery, userFilter, dateFilter]);

  const fetchHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await api.get(`/api/v1/reconciliation/history/${reportId}`);
      setHistory(data.history || []);
    } catch (err: any) {
      console.error('Failed to fetch history:', err);
      setError(err.message || 'Failed to load reclassification history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...history];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        entry =>
          entry.vendor.toLowerCase().includes(query) ||
          entry.from_category.toLowerCase().includes(query) ||
          entry.to_category.toLowerCase().includes(query) ||
          entry.reason.toLowerCase().includes(query) ||
          entry.user.toLowerCase().includes(query)
      );
    }

    // User filter
    if (userFilter !== 'all') {
      filtered = filtered.filter(entry => entry.user === userFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.timestamp);

        if (dateFilter === 'today') {
          return entryDate >= today;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return entryDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return entryDate >= monthAgo;
        }
        return true;
      });
    }

    setFilteredHistory(filtered);
  };

  const uniqueUsers = Array.from(new Set(history.map(entry => entry.user)));

  const handleExportCSV = () => {
    // Generate CSV content
    const headers = [
      'Date',
      'Transaction ID',
      'Transaction Date',
      'Vendor',
      'Amount',
      'From Category',
      'To Category',
      'Reason',
      'User',
      'User Email'
    ];

    const rows = filteredHistory.map(entry => [
      new Date(entry.timestamp).toLocaleString(),
      entry.transaction_id,
      new Date(entry.transaction_date).toLocaleDateString(),
      entry.vendor,
      formatCurrency(entry.amount),
      entry.from_category,
      entry.to_category,
      entry.reason,
      entry.user,
      entry.user_email
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reclassification_history_${reportId.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Reclassification Audit Trail" className="rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Reclassification Audit Trail</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Complete history of all transaction reclassifications
            </p>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                <input
                  type="text"
                  placeholder="Search vendor, category, reason, or user..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                />
              </div>
            </div>

            {/* User Filter */}
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:border-transparent"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              <option value="all">All Users</option>
              {uniqueUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:border-transparent"
              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>

            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={filteredHistory.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>

          {/* Result Count */}
          <div className="mt-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Showing <strong>{filteredHistory.length}</strong> of <strong>{history.length}</strong> entries
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-brand)' }}></div>
                <p style={{ color: 'var(--color-text-muted)' }}>Loading history...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="border rounded-lg p-6 max-w-md" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <p className="font-medium mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Error Loading History</p>
                <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
              </div>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
                <p className="font-medium" style={{ color: 'var(--color-text-muted)' }}>No reclassifications found</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {searchQuery || userFilter !== 'all' || dateFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Reclassification history will appear here'}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[color:var(--color-border-faint)]">
              {filteredHistory.map((entry, idx) => (
                <div key={idx} className="p-6 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left Side - Transaction Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{entry.vendor}</span>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>•</span>
                        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {new Date(entry.transaction_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>•</span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          <FinancialValue value={entry.amount} />
                        </span>
                      </div>

                      {/* Reclassification Details */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-block px-2 py-1 text-xs rounded line-through" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          {entry.from_category}
                        </span>
                        <span style={{ color: 'var(--color-text-dim)' }}>→</span>
                        <span className="inline-block px-2 py-1 text-xs rounded font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                          {entry.to_category}
                        </span>
                      </div>

                      {/* Reason */}
                      <p className="text-sm italic mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                        <strong className="not-italic text-xs uppercase" style={{ color: 'var(--color-text-label)' }}>Reason:</strong> {entry.reason}
                      </p>
                    </div>

                    {/* Right Side - User & Timestamp */}
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end mb-1">
                        <User className="w-3 h-3" style={{ color: 'var(--color-text-dim)' }} />
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{entry.user}</span>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{entry.user_email}</p>
                      <p className="text-xs mt-2" style={{ color: 'var(--color-text-dim)' }}>
                        {new Date(entry.timestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
